import { AfterViewInit, Component, HostBinding, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Subscription } from 'rxjs';

import { EmailPreviewRefreshService } from '../../services/email-preview-refresh.service';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { CommonModule } from '@angular/common';

interface ReviewSource {
  name: string;
  sourceId: string;
}

const SuggestedReviewSources: Map<string, ReviewSource> = new Map<string, ReviewSource>([
  [
    'google',
    {
      name: 'EMAIL_BUILDER.REVIEW_REQUEST_LINKS_BLOCK.SOURCES.GOOGLE',
      sourceId: '10010',
    },
  ],
  [
    'facebook',
    {
      name: 'EMAIL_BUILDER.REVIEW_REQUEST_LINKS_BLOCK.SOURCES.FACEBOOK',
      sourceId: '10050',
    },
  ],
]);

// Interface
export interface ReviewRequestLinksBlock {
  sources: string[];
  rawHtml: string;
}

const defaultSources = [SuggestedReviewSources.get('google').sourceId, SuggestedReviewSources.get('facebook').sourceId];

// Default blank value
export const blankReviewRequestLinksBlock: ReviewRequestLinksBlock = {
  sources: defaultSources,
  rawHtml: getRawHtml(defaultSources),
};

function getRawHtml(sources: string[]): string {
  const htmlContent = sources
    .map(
      (source) =>
        `{% if reviewRequestCTA['` +
        source +
        `'] %}<div style="display: inline-block; margin:5px">{{ reviewRequestCTA['` +
        source +
        `'] }}</div>{% endif %}`,
    )
    .join();
  return `<div style="text-align: center">
    ${htmlContent}
</div>`;
}

@Component({
  selector: 'email-review-request-links-block',
  templateUrl: './review-request-links-block.component.html',
  styleUrls: ['./review-request-links-block.component.scss'],
  standalone: true,
  imports: [MatCheckboxModule, TranslateModule, GalaxyFormFieldModule, FormsModule, CommonModule],
})
export class ReviewRequestLinksBlockComponent implements AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'email-review-request-links-block';
  @Input() content: ReviewRequestLinksBlock;
  @ViewChild('blockInputs') blockInputs: NgForm;

  reviewSources: ReviewSource[] = [...SuggestedReviewSources.values()];

  private readonly subscriptions: Subscription[] = [];

  constructor(private emailPreview: EmailPreviewRefreshService) {}

  ngAfterViewInit(): void {
    this.subscriptions.push(
      this.blockInputs.form.valueChanges.subscribe(() => {
        this.emailPreview.refresh();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  isSourceSelected(sourceId: string): boolean {
    return !!this.content.sources.find((x) => x === sourceId);
  }

  valueChange(sourceId: string, $event): void {
    if ($event.checked) {
      this.content.sources.push(sourceId);
    } else {
      this.content.sources = this.content.sources.filter((x) => x !== sourceId);
    }
    this.content.rawHtml = getRawHtml(this.content.sources);
    this.emailPreview.refresh();
  }
}
